import { useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { FC, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  getListDocumentsQueryKey,
  useDeleteDocument,
} from "~/api/default/default";

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import { useOrganizationParams } from "~/helpers";
import { useToast } from "~/hooks/use-toast";
import { trackEvent } from "~/utils/analytics";

interface DeleteDocumentAlertDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  documentId: string;
  documentName: string;
  caseId: string;
}

export const DeleteDocumentAlertDialog: FC<DeleteDocumentAlertDialogProps> = ({
  open,
  setOpen,
  documentId,
  documentName,
  caseId,
}) => {
  const { organizationId } = useOrganizationParams();
  const { mutateAsync: deleteDocument } = useDeleteDocument();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const intl = useIntl();
  const [isDeleting, setIsDeleting] = useState(false);

  const onDeleteDocument = async () => {
    try {
      setIsDeleting(true);

      trackEvent({
        eventName: "app/case/document.delete/initiated",
        properties: {
          caseId,
          documentId,
          documentName,
        },
      });

      await deleteDocument({
        organizationId,
        projectId: caseId,
        documentId,
      });

      await queryClient.invalidateQueries({
        queryKey: getListDocumentsQueryKey(organizationId, caseId),
      });

      trackEvent({
        eventName: "app/case/document.delete/success",
        properties: {
          caseId,
          documentId,
          documentName,
        },
      });

      setOpen(false);
    } catch (error: any) {
      trackEvent({
        eventName: "app/case/document.delete/failed",
        properties: {
          caseId,
          documentId,
          documentName,
          errorMessage: error?.message || "Unknown error",
        },
      });

      toast({
        title: intl.formatMessage({ id: "error", defaultMessage: "Error" }),
        description: intl.formatMessage({
          id: "deleteDocument.failed",
          defaultMessage: "Failed to delete the document. Please try again.",
        }),
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            <FormattedMessage
              id="caseDocument.deleteDialog.title"
              defaultMessage="Delete Document"
            />
          </AlertDialogTitle>
          <AlertDialogDescription>
            <FormattedMessage
              id="caseDocument.deleteDialog.description"
              defaultMessage="Are you sure you want to delete {docName}? This action cannot be undone."
              values={{
                docName: <strong>{documentName}</strong>,
              }}
            />
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            onClick={() => setOpen(false)}
            disabled={isDeleting}
          >
            <FormattedMessage id="cancel" defaultMessage="Cancel" />
          </AlertDialogCancel>
          <Button
            onClick={onDeleteDocument}
            disabled={isDeleting}
            aria-busy={isDeleting}
            variant="destructive"
          >
            {isDeleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            <FormattedMessage id="delete" defaultMessage="Delete" />
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
